import { Sparkles } from "lucide-react";
import products from "@/data/iap-products.json";
import { getItemName } from "@/data/item-names";
import { CurrencyIcon, HeroPortrait, ItemIcon } from "@/components/GameIcon";
import { HERO_NAMES } from "@/types/cloud-data";

type Reward = { kind: string; amount?: number; itemId?: string | null; heroId?: number };
const catalog = new Map<string, { id: string; rewards: Reward[] }>();
for (const product of products as { id: string; aliases: string[]; rewards: Reward[] }[])
  for (const alias of [product.id, ...product.aliases]) catalog.set(alias, product);

const titleCase = (value: string) => value.replace(/_ios$/, "").split("_").filter(Boolean)
  .map(word => /^\d+$/.test(word) ? word : word[0].toUpperCase() + word.slice(1)).join(" ");

/** A readable store name from the client catalogue; unknown IDs are title-cased, never guessed. */
export function productName(productId: string) {
  const product = catalog.get(productId);
  const rewards = product?.rewards ?? [];
  const only = rewards.length === 1 ? rewards[0] : null;
  if (only?.kind === "Special_Hero" && only.heroId != null && HERO_NAMES[only.heroId]) return `Unlock ${HERO_NAMES[only.heroId]}`;
  if (only?.kind === "Ruby") return `${(only.amount ?? 1).toLocaleString()} Ruby`;
  if (only?.kind === "Gold") return `${(only.amount ?? 1).toLocaleString()} Gold`;
  if (only?.kind === "Item" && only.itemId) return `${(only.amount ?? 1).toLocaleString()} × ${getItemName(only.itemId)}`;
  return titleCase(product?.id ?? productId);
}

export function ProductArt({ productId, className }: { productId: string; className?: string }) {
  const rewards = catalog.get(productId)?.rewards ?? [];
  const hero = rewards.find(reward => reward.kind === "Special_Hero" && reward.heroId != null);
  if (hero) return <HeroPortrait heroId={hero.heroId!} className={className} />;
  const item = rewards.find(reward => reward.kind === "Item" && reward.itemId);
  if (rewards.length === 1 && item) return <ItemIcon id={item.itemId} className={className} />;
  if (rewards.some(reward => reward.kind === "Ruby")) return <CurrencyIcon currency="ruby" className={className} />;
  if (item) return <ItemIcon id={item.itemId} className={className} />;
  if (rewards.some(reward => reward.kind === "Gold")) return <CurrencyIcon currency="gold" className={className} />;
  return <span className={`game-icon pu-product-fallback ${className ?? ""}`} aria-hidden="true"><Sparkles size={16} /></span>;
}
